import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  CheckCircle2,
  Download,
  Eye,
  FileCheck2,
  FileClock,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSearchParams} from '../router';
import {api, apiRequest, fetchAuthenticatedBlob} from '../api';
import {Modal, PrimaryButton, QuietButton, SecondaryButton, Surface} from '../design';
import {DocumentArtifact} from '../types';
import {EmptyPanel, ErrorPanel, LoadingPanel, PageHeader, formatRelativeDate} from '../components/PagePrimitives';
import {PdfJsPreview} from '../components/PdfJsPreview';
type ApprovedBulletProvenance = {
  bulletId: string;
  bulletVersion: number;
  exactText: string;
  factIds: string[];
  facts?: Array<{factId: string; factVersion: number; sourceRefs: string[]}>;
};

type DocumentProvenance = {
  documentId: string;
  sourceKey?: string;
  conservativeRenderer: boolean;
  renderer?: 'resume_source_informed_v1' | 'resume_conservative_v2' | string;
  fidelityAssessment?: {
    version: 1;
    mode: 'source_informed' | 'conservative';
    renderer: string;
    threshold: number;
    score: number;
    eligible: boolean;
    source: 'pdf_text_geometry' | 'docx_style_xml' | 'unavailable';
    reasons: string[];
    preservedAttributes: string[];
    sourceProfile?: {
      fontFamily: 'sans' | 'serif' | 'mono';
      bodyFontSizePoints: number;
      headingFontSizePoints: number;
      pageWidthPoints: number;
      pageHeightPoints: number;
      marginTopPoints: number;
      marginRightPoints: number;
      marginBottomPoints: number;
      marginLeftPoints: number;
      columnCount: 1 | 2;
    };
  };
  fidelityWarning?: string;
  provenance: ApprovedBulletProvenance[];
};

const readableMetadata = (value: string): string => value.replaceAll('_', ' ');

const hasExactApprovedBulletProvenance = (value?: DocumentProvenance): boolean =>
  Boolean(value && Array.isArray(value.provenance) && value.provenance.length) &&
  value!.provenance.every((entry) =>
    Boolean(
      typeof entry.bulletId === 'string' &&
      entry.bulletId &&
      Number.isInteger(entry.bulletVersion) &&
      entry.bulletVersion > 0 &&
      typeof entry.exactText === 'string' &&
      entry.exactText.trim() &&
      Array.isArray(entry.factIds) &&
      entry.factIds.length &&
      (entry.facts === undefined ||
        (Array.isArray(entry.facts) &&
          entry.facts.every((fact) => Array.isArray(fact.sourceRefs)))),
    ),
  );

export const DocumentsPage = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const focusedRowRef = useRef<HTMLDivElement | null>(null);
  const [preview, setPreview] = useState<DocumentArtifact | null>(null);
  const [securePreviewUrl, setSecurePreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [pdfReady, setPdfReady] = useState(false);
  const documents = useQuery({queryKey: ['documents'], queryFn: api.documents});
  const requestedFocusId = searchParams.get('focus')?.trim() ?? '';
  const focusedDocumentId = requestedFocusId && documents.data?.items.some(
    (document) => document.id === requestedFocusId,
  ) ? requestedFocusId : '';
  const provenance = useQuery({
    queryKey: ['document-provenance', preview?.id, preview?.currentVersionId],
    queryFn: () =>
      apiRequest<DocumentProvenance>(
        '/documents/' + encodeURIComponent(preview!.id) + '/provenance',
      ),
    enabled: Boolean(preview?.id),
  });
  const provenanceReady = hasExactApprovedBulletProvenance(provenance.data);
  const handlePdfReady = useCallback(() => setPdfReady(true), []);
  const handlePdfError = useCallback((message: string) => {
    setPdfReady(false);
    setPreviewError(message);
  }, []);

  useEffect(() => {
    const row = focusedDocumentId ? focusedRowRef.current : null;
    if (!row) return;
    row.scrollIntoView({block: 'center'});
    row.focus({preventScroll: true});
  }, [focusedDocumentId]);

  const approve = useMutation({
    mutationFn: (document: DocumentArtifact) =>
      apiRequest<DocumentArtifact>('/documents/' + encodeURIComponent(document.id) + '/approve', {
        method: 'POST',
        body: JSON.stringify({
          ...(document.currentVersionId ? {versionId: document.currentVersionId} : {}),
          ...(document.currentVersionSha256 ? {sha256: document.currentVersionSha256} : {}),
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['documents']});
      await queryClient.invalidateQueries({queryKey: ['action-items']});
    },
  });

  const download = useMutation({
    mutationFn: async (documentArtifact: DocumentArtifact) => {
      if (!documentArtifact.downloadUrl) throw new Error('This document is not available for download.');
      const blob = await fetchAuthenticatedBlob(documentArtifact.downloadUrl);
      const objectUrl = URL.createObjectURL(blob);
      try {
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = documentArtifact.name;
        link.click();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    },
  });

  useEffect(() => {
    const sourceUrl = preview?.previewUrl;
    if (!sourceUrl) {
      setSecurePreviewUrl('');
      setPreviewError('');
      setPdfReady(false);
      return;
    }
    setPdfReady(false);

    const controller = new AbortController();
    let cancelled = false;
    let objectUrl = '';
    setSecurePreviewUrl('');
    setPreviewError('');

    void fetchAuthenticatedBlob(sourceUrl, {signal: controller.signal})
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSecurePreviewUrl(objectUrl);
      })
      .catch((error: unknown) => {
        if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) return;
        setPreviewError(error instanceof Error ? error.message : 'The protected PDF could not be loaded.');
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [preview?.previewUrl]);

  return (
    <>
      <PageHeader
        eyebrow="Immutable after approval"
        title="See the exact artifact before it leaves your desk."
        description="Source files, tailored résumés, cover letters, and supporting documents keep version history and provenance. Approved or submitted versions never change in place."
      />

      <Surface className="pd-trust-banner">
        <ShieldCheck size={23} aria-hidden="true" />
        <div>
          <strong>Approved bullets only</strong>
          <p>Final résumés can use only the exact bullet versions you approved and confirmed factual metadata.</p>
        </div>
      </Surface>

      {documents.isLoading ? <LoadingPanel label="Loading documents" /> : null}
      {documents.isError ? <ErrorPanel error={documents.error} retry={() => void documents.refetch()} /> : null}
      {documents.data && documents.data.items.length === 0 ? (
        <EmptyPanel
          title="No document artifacts yet."
          description="Your source résumé will appear after onboarding. Create tailored artifacts only for opportunities you intend to pursue."
        />
      ) : null}

      <div className="pd-document-list">
        {documents.data?.items.map((document) => (
          <Surface
            key={document.id}
            ref={document.id === focusedDocumentId ? focusedRowRef : undefined}
            className={'pd-document-row' + (document.id === focusedDocumentId ? ' is-focused' : '')}
            tabIndex={document.id === focusedDocumentId ? -1 : undefined}
          >
            <div className="pd-document-icon" aria-hidden="true">
              {document.state === 'approved' || document.state === 'submitted' ? (
                <FileCheck2 size={25} />
              ) : document.state === 'running' || document.state === 'queued' ? (
                <FileClock size={25} />
              ) : (
                <FileText size={25} />
              )}
            </div>
            <div className="pd-document-copy">
              <div className="pd-card-topline">
                <span>{document.kind.replaceAll('_', ' ')}</span>
                <strong>{document.state}</strong>
              </div>
              <h3>{document.name}</h3>
              <p>
                Version {document.version} · {document.sizeBytes === undefined ? 'size unavailable' : `${(document.sizeBytes / 1024).toFixed(0)} KB`} ·{' '}
                created {formatRelativeDate(document.createdAt)}
              </p>
              {document.approvedAt ? (
                <span className="pd-approved-stamp">
                  <CheckCircle2 size={15} aria-hidden="true" />
                  Approved {formatRelativeDate(document.approvedAt)}
                </span>
              ) : null}
            </div>
            <div className="pd-document-actions">
              {document.previewUrl ? (
                <SecondaryButton leftSection={<Eye size={16} />} onClick={() => setPreview(document)}>
                  Review exact PDF
                </SecondaryButton>
              ) : null}
              {document.downloadUrl ? (
                <QuietButton
                  leftSection={<Download size={16} />}
                  loading={download.isPending && download.variables?.id === document.id}
                  onClick={() => download.mutate(document)}
                >
                  Download
                </QuietButton>
              ) : null}
              {(document.state === 'draft' || document.state === 'waiting_for_user') && !document.previewUrl ? (
                <PrimaryButton
                  loading={approve.isPending && approve.variables?.id === document.id}
                  onClick={() => approve.mutate(document)}
                >
                  Approve this version
                </PrimaryButton>
              ) : null}
            </div>
          </Surface>
        ))}
      </div>

      {download.isError ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {download.error instanceof Error ? download.error.message : 'The protected document could not be downloaded.'}
        </p>
      ) : null}
      {approve.isError ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {approve.error instanceof Error ? approve.error.message : 'The document could not be approved.'}
        </p>
      ) : null}

      <Modal
        opened={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={preview ? 'Review ' + preview.name : 'Review PDF'}
        size="calc(100vw - 3rem)"
        className="pd-pdf-modal"
      >
        {preview?.previewUrl ? (
          <>
            <div className="pd-pdf-review-note">
              This is the exact PDF that will be attached. Inspect every page before approving it.
            </div>
            <section className="pd-pdf-provenance" aria-labelledby="pdf-provenance-heading">
              <div className="pd-note-heading">
                <div>
                  <span className="pd-eyebrow">Exact source review</span>
                  <h3 id="pdf-provenance-heading">Approved bullet provenance</h3>
                </div>
                {provenance.data?.sourceKey ? (
                  <span>Source set {provenance.data.sourceKey.slice(0, 10)}</span>
                ) : null}
              </div>
              {provenance.isLoading ? <LoadingPanel label="Loading approved bullet provenance" /> : null}
              {provenance.isError ? (
                <p className="pd-form-error" role="alert">
                  {provenance.error instanceof Error
                    ? provenance.error.message
                    : 'Approved bullet provenance could not be loaded.'}
                </p>
              ) : null}
              {provenance.data?.fidelityAssessment ? (
                <section className="pd-fidelity-assessment" aria-labelledby="layout-fidelity-heading">
                  <h4 id="layout-fidelity-heading">Layout fidelity assessment</h4>
                  <dl>
                    <div>
                      <dt>Rendering path</dt>
                      <dd>{provenance.data.fidelityAssessment.mode === 'source_informed'
                        ? 'Source-informed reconstruction'
                        : 'Conservative fallback'}</dd>
                    </div>
                    <div>
                      <dt>Measured confidence</dt>
                      <dd>
                        {Math.round(provenance.data.fidelityAssessment.score * 100)}% · threshold{' '}
                        {Math.round(provenance.data.fidelityAssessment.threshold * 100)}%
                      </dd>
                    </div>
                    <div>
                      <dt>Renderer</dt>
                      <dd>{provenance.data.fidelityAssessment.renderer}</dd>
                    </div>
                    <div>
                      <dt>Measurement source</dt>
                      <dd>{readableMetadata(provenance.data.fidelityAssessment.source)}</dd>
                    </div>
                    <div>
                      <dt>Preserved</dt>
                      <dd>{provenance.data.fidelityAssessment.preservedAttributes.map(readableMetadata).join(', ')}</dd>
                    </div>
                    {provenance.data.fidelityAssessment.sourceProfile ? (
                      <div>
                        <dt>Source measurements</dt>
                        <dd>
                          {provenance.data.fidelityAssessment.sourceProfile.fontFamily} family ·{' '}
                          {provenance.data.fidelityAssessment.sourceProfile.bodyFontSizePoints} pt body /{' '}
                          {provenance.data.fidelityAssessment.sourceProfile.headingFontSizePoints} pt heading ·{' '}
                          {provenance.data.fidelityAssessment.sourceProfile.pageWidthPoints} ×{' '}
                          {provenance.data.fidelityAssessment.sourceProfile.pageHeightPoints} pt page · margins{' '}
                          {[
                            provenance.data.fidelityAssessment.sourceProfile.marginTopPoints,
                            provenance.data.fidelityAssessment.sourceProfile.marginRightPoints,
                            provenance.data.fidelityAssessment.sourceProfile.marginBottomPoints,
                            provenance.data.fidelityAssessment.sourceProfile.marginLeftPoints,
                          ].join('/')} pt · {provenance.data.fidelityAssessment.sourceProfile.columnCount}{' '}
                          {provenance.data.fidelityAssessment.sourceProfile.columnCount === 1 ? 'column' : 'columns'}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {provenance.data.fidelityAssessment.reasons.length ? (
                    <p>
                      Assessment notes: {provenance.data.fidelityAssessment.reasons.map(readableMetadata).join(', ')}.
                    </p>
                  ) : null}
                </section>
              ) : null}
              {provenance.data?.fidelityWarning ? (
                <p className="pd-pdf-fidelity-warning">{provenance.data.fidelityWarning}</p>
              ) : null}
              {provenanceReady && provenance.data ? (
                <ol className="pd-provenance-list">
                  {provenance.data.provenance.map((entry) => (
                    <li key={entry.bulletId + ':' + entry.bulletVersion}>
                      <blockquote>{entry.exactText}</blockquote>
                      <dl>
                        <div>
                          <dt>Approved bullet</dt>
                          <dd>{entry.bulletId} · version {entry.bulletVersion}</dd>
                        </div>
                        <div>
                          <dt>Confirmed facts</dt>
                          <dd>{entry.factIds.join(', ')}</dd>
                        </div>
                        {entry.facts?.flatMap((fact) => fact.sourceRefs).length ? (
                          <div>
                            <dt>Evidence sources</dt>
                            <dd>{entry.facts!.flatMap((fact) => fact.sourceRefs).join(', ')}</dd>
                          </div>
                        ) : null}
                      </dl>
                    </li>
                  ))}
                </ol>
              ) : null}
              {provenance.isSuccess && !provenanceReady ? (
                <p className="pd-form-error" role="alert">
                  Approval is unavailable because this PDF has no complete approved-bullet provenance.
                </p>
              ) : null}
            </section>
            {(!securePreviewUrl || !pdfReady) && !previewError ? <LoadingPanel label="Rendering protected PDF" /> : null}
            {previewError ? <p className="pd-form-error" role="alert">{previewError}</p> : null}
            {securePreviewUrl ? (
              <PdfJsPreview
                source={securePreviewUrl}
                title={'PDF preview of ' + preview.name}
                onReady={handlePdfReady}
                onError={handlePdfError}
              />
            ) : null}
            <div className="pd-form-actions">
              <SecondaryButton onClick={() => setPreview(null)}>Close review</SecondaryButton>
              {preview.state === 'draft' || preview.state === 'waiting_for_user' ? (
                <PrimaryButton
                  loading={approve.isPending}
                  disabled={!securePreviewUrl || !pdfReady || !provenanceReady}
                  onClick={() => {
                    if (!securePreviewUrl || !pdfReady || !provenanceReady) return;
                    approve.mutate(preview, {onSuccess: () => setPreview(null)});
                  }}
                >
                  Approve exact PDF
                </PrimaryButton>
              ) : null}
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
};
